import { useState, useEffect } from "react";
import { useSEO } from "@/hooks/use-seo";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowDown, CheckCircle2, Lock, Compass, Megaphone, Sparkles, Mail, BarChart3, Radar } from "lucide-react";
import Footer from "@/components/Footer";
import ExpertContact from "@/components/ExpertContact";
import { HeroBackground } from "@/components/HeroBackground";
import logoW from "@assets/W_1771840384312.webp";

import amayaLogo from "@assets/images_(2)_1771842328134.png";
import newhouseLogo from "@assets/newhouse-logo-update-03_1771842328135.webp";
import featherWomenLogo from "@assets/feather_women_logo_1771842328135.jpeg";
import frankyLogo from "@assets/franky-logo-bold-zwart-mu3lf19d-2022-07-18-nzrevxra-2022-08-16_1771842328135.png";
import equalsLogo from "@assets/Equals_logo_1771842328135.webp";
import brinckersLogo from "@assets/goed-3_1771842328135.png";
import juridischLoketLogo from "@assets/HJL_1771842328135.jpeg";
import jutkaRiskaLogo from "@assets/images-3_1771842328135.png";
import mimiEtToiLogo from "@assets/images_1771842328135.png";
import pigHenLogo from "@assets/images_(1)_1771842328135.png";

import imgBannerGoed from "@assets/Kopie_van_MOBIEL_BANNER-4_16e91bfa-1908-40e0-9361-0a3facde1be_1772027139538.webp";
import imgAmayaCard from "@assets/amaya-card.webp";
import imgFamilyBike from "@assets/artPic_public_L_3495432_1771842615568.webp";
import imgJewelry from "@assets/felicitysilverbeverlysilver-min_1771842615568.webp";
import imgFeather from "@assets/geather_1771842615568.webp";
import imgJutka from "@assets/jutka_1771842615568.webp";

import imgMimiCover from "@assets/mimi_et_toi_cover_1771842892045.jpeg";
import imgMimiDetail from "@assets/mimi_1771842892046.webp";
import imgPigHen from "@assets/pighen_1771842892046.jpg";

import zevyLogo from "@assets/zevy_1771843123471.jpg";
import vedderLogo from "@assets/vedder-vedder_1771843123471.jpg";
import avatarVedder from "@assets/avatar-vedder.jpg";
import imgVedderCase from "@assets/vedder-case.jpg";
import signalReport from "@assets/signal-report.webp";
import mattLogo from "@assets/shopimage_1771843123471.png";

import moddernLogo from "@assets/Scherm­afbeelding_2026-03-25_om_13.42.34_1774442580194.png";
import stripZevy from "@assets/strip-zevy.png";
import stripVedder from "@assets/strip-vedder.png";
import stripMatt from "@assets/strip-matt.png";
import stripAmaya from "@assets/strip-amaya.png";
import stripNewhouse from "@assets/strip-newhouse.png";
import stripFeather from "@assets/strip-feather.png";
import stripFranky from "@assets/strip-franky.png";
import stripEquals from "@assets/strip-equals.png";
import stripBrinckers from "@assets/strip-brinckers.png";
import stripJuridischLoket from "@assets/strip-juridischloket.png";
import stripJutkaRiska from "@assets/strip-jutkariska.png";
import stripMimi from "@assets/strip-mimi.png";
import stripPigHen from "@assets/strip-pighen.png";
import stripModdern from "@assets/strip-moddern.png";
import imgModdernCase from "@assets/moddern-case.webp";
import adKoiVideo from "@assets/autumn_1350x1080_1774356682569.mp4";
import adModdern1 from "@assets/ad-moddern-1.mp4";
import adModdern2 from "@assets/ad-moddern-2.mp4";
import adMimiSoleil from "@assets/ad-mimi-soleil.mp4";
import avatarAlrik from "@assets/avatar-alrik.jpg";
import avatarPien from "@assets/avatar-pien.jpg";
import avatarFranka from "@assets/avatar-franka.jpg";
import avatarMimi from "@assets/avatar-mimi.jpg";

import { Link, useLocation } from "wouter";
import { useLanguage } from "@/contexts/LanguageContext";
import Navbar from "@/components/Navbar";

// Components
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Carousel, CarouselContent, CarouselItem, type CarouselApi } from "@/components/ui/carousel";

const RELATION_BASE_MONTHS = 25;
const RELATION_BASE_DATE = new Date(2026, 6, 1);

function relationMonths(): number {
  const now = new Date();
  const elapsed = (now.getFullYear() - RELATION_BASE_DATE.getFullYear()) * 12 + (now.getMonth() - RELATION_BASE_DATE.getMonth());
  return RELATION_BASE_MONTHS + Math.max(0, elapsed);
}

const REEL_ADS = [
  { src: adKoiVideo, handle: "koiatelier", initials: "KA" },
  { src: adModdern1, handle: "moddern", initials: "M" },
  { src: adMimiSoleil, handle: "mimiettoi", initials: "MT" },
  { src: adModdern2, handle: "moddern", initials: "M" },
];

function AdReel() {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setTimeout(() => setIdx((i) => (i + 1) % REEL_ADS.length), 11000);
    return () => clearTimeout(t);
  }, [idx]);
  const ad = REEL_ADS[idx];
  return (
    <div className="relative h-full w-full overflow-hidden rounded-[34px] bg-black">
      <AnimatePresence>
        <motion.video
          key={idx}
          src={ad.src}
          autoPlay
          muted
          loop
          playsInline
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.6 }}
          className="absolute inset-0 h-full w-full object-cover"
        />
      </AnimatePresence>
      <div className="absolute top-4 left-4 right-4 flex items-center gap-2">
        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-white/15 backdrop-blur text-[10px] font-bold text-white">{ad.initials}</span>
        <span className="flex flex-col leading-tight">
          <span className="text-xs font-semibold text-white">{ad.handle}</span>
          <span className="text-[11px] text-white/70">Sponsored</span>
        </span>
      </div>
      <div className="absolute bottom-5 left-0 right-0 flex flex-col items-center gap-1">
        <span className="text-[13px] text-white/85 leading-none">⌃</span>
        <span className="text-[12px] font-bold tracking-[0.12em] text-white">LEARN MORE</span>
      </div>
    </div>
  );
}

function MonthsCounter() {
  const [val, setVal] = useState(0);
  const target = relationMonths();
  return (
    <motion.span
      className="font-semibold text-[#4CEAAC]"
      onViewportEnter={() => {
        const start = performance.now();
        const dur = 1200;
        const tick = (t: number) => {
          const p = Math.min(1, (t - start) / dur);
          setVal(Math.round(target * (1 - Math.pow(1 - p, 3))));
          if (p < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      }}
      viewport={{ once: true }}
    >{val}</motion.span>
  );
}

type ApproachPillar = { num: string; title: string; desc: string; visual: string; stack: boolean };

function ApproachCard({ pillar, index, nl }: { pillar: ApproachPillar; index: number; nl: boolean }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      whileHover={{ y: -6 }}
      className="has-grain relative flex h-full flex-col overflow-hidden rounded-[20px] border border-white/[0.09] bg-white/[0.03] p-6 sm:p-8 lg:p-10 hover:border-[#4CEAAC]/30 transition-all duration-500 hover:shadow-[0_16px_56px_rgba(0,0,0,0.45),0_0_56px_rgba(76,234,172,0.12)]"
      data-testid={`approach-pillar-${index}`}
    >
      <span className="relative text-[34px] lg:text-[44px] leading-none tracking-[-0.02em] font-display font-extrabold text-[#4CEAAC] [text-shadow:0_0_32px_rgba(76,234,172,0.35)]">{pillar.num}</span>
      <h4 className="relative text-[21px] lg:text-[27px] tracking-[-0.015em] font-display font-bold mt-4 lg:mt-5 mb-2.5 lg:mb-3">{pillar.title}</h4>
      <p className="relative text-[15px] lg:text-[16.5px] text-white/70 leading-[1.6] lg:leading-[1.65]">{pillar.desc}</p>
      {pillar.stack && (
        <div className="relative mt-auto pt-5 lg:pt-6">
          <div className="border-t border-white/10 pt-4">
            <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.16em] text-white/50">{nl ? 'Gekoppeld met je stack' : 'Connected to your stack'}</p>
            <div className="flex flex-wrap items-center gap-2">
              {["Shopify", "Klaviyo", "Meta", "Google Ads", "TikTok Ads"].map((l, k) => (
                <span key={k} className="rounded-full border border-white/[0.12] bg-white/[0.06] px-3 py-1 lg:px-3.5 lg:py-1.5 text-[12px] lg:text-[13px] font-semibold text-white/85">{l}</span>
              ))}
            </div>
          </div>
        </div>
      )}
    </motion.div>
  );
}

export default function Home() {
  const [scrolled, setScrolled] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [approachApi, setApproachApi] = useState<CarouselApi>();
  const [approachIndex, setApproachIndex] = useState(0);
  const [approachCount, setApproachCount] = useState(0);
  useEffect(() => {
    if (!approachApi) return;
    setApproachCount(approachApi.scrollSnapList().length);
    setApproachIndex(approachApi.selectedScrollSnap());
    const onSelect = () => setApproachIndex(approachApi.selectedScrollSnap());
    approachApi.on("select", onSelect);
    approachApi.on("reInit", onSelect);
    return () => { approachApi.off("select", onSelect); approachApi.off("reInit", onSelect); };
  }, [approachApi]);

  const [, setLocation] = useLocation();
  const { t, toggleLanguage, language, localePath } = useLanguage();
  const nl = language === 'nl';

  const approachPillars: ApproachPillar[] = nl ? [
    { num: "01", title: "Senior strategen", desc: "Elke klant werkt direct met een strateeg met 10 jaar ervaring in e-commerce. Dagelijks echte aandacht, korte lijnen en één aanspreekpunt dat jouw cijfers kent.", visual: "w", stack: false },
    { num: "02", title: "AI-first aanpak", desc: "Onze eigen AI-tooling laat ons sneller schakelen: campagnes optimaliseren, creatives analyseren en de competitie volgen. Via koppelingen met Shopify, Klaviyo en Meta zie je in één dashboard wat elke euro oplevert. Wrkt Signal is de motor.", visual: "radar", stack: true },
    { num: "03", title: "Bewezen werkwijze", desc: "Het Growth Compass vertaalt 15 jaar advertising-ervaring naar één helder proces: van audit en forecast tot executie en retentie. Je weet elke maand waar je staat en wat de volgende stap is.", visual: "compass", stack: false },
    { num: "04", title: "100% D2C e-commerce", desc: "We werken uitsluitend voor D2C e-commerce merken in fashion, beauty, sieraden en lifestyle. Elke benchmark, elk creative-inzicht en elke forecast komt uit jouw markt. Die focus zie je terug in snelheid en resultaat.", visual: "focus", stack: false },
  ] : [
    { num: "01", title: "Senior strategists", desc: "Every client works directly with a strategist with 10 years of e-commerce experience. Real attention every day, short lines and one point of contact who knows your numbers.", visual: "w", stack: false },
    { num: "02", title: "AI-first approach", desc: "Our own AI tooling lets us move faster: optimizing campaigns, analyzing creatives and tracking the competition. Through integrations with Shopify, Klaviyo and Meta you see what every euro delivers in one dashboard. Wrkt Signal is the engine.", visual: "radar", stack: true },
    { num: "03", title: "Proven method", desc: "The Growth Compass translates 15 years of advertising experience into one clear way of working: from audit and forecast to execution and retention. Every month you know where you stand and what the next step is.", visual: "compass", stack: false },
    { num: "04", title: "100% D2C e-commerce", desc: "We work exclusively with D2C e-commerce brands in fashion, beauty, jewelry and lifestyle. Every benchmark, creative insight and forecast comes from your market. You see that focus in speed and results.", visual: "focus", stack: false },
  ];

  useSEO({
    title: nl
      ? 'Wrkt Digital | Digital Marketing Bureau Amsterdam | D2C E-commerce Growth Agency'
      : 'Wrkt Digital | Digital Marketing Agency Amsterdam | D2C E-commerce Growth Agency',
    description: nl
      ? 'Wrkt Digital: experts in het strategisch groeien van D2C e-commerce brands. Digital marketing bureau in Amsterdam, gespecialiseerd in Meta Ads, TikTok Advertising, Google Ads & Klaviyo, versterkt met eigen AI-tooling. Gratis Growth Scan beschikbaar.'
      : 'Wrkt Digital: experts in strategically growing D2C e-commerce brands. Digital marketing agency in Amsterdam, specialized in Meta Ads, TikTok Advertising, Google Ads & Klaviyo, amplified by proprietary AI tooling. Free Growth Scan available.',
    path: '/',
    lang: nl ? 'nl' : 'en',
    keywords: nl
      ? 'digital marketing bureau amsterdam, online marketing bureau, performance marketing agency, Meta Ads bureau, TikTok advertising Nederland, Pinterest Ads bureau, Klaviyo partner, AI marketing bureau, growth agency, D2C e-commerce marketing, social media advertising, Google Ads bureau Amsterdam, e-commerce growth agency, data-driven marketing, conversie optimalisatie, remarketing bureau, digital advertising Amsterdam'
      : 'digital marketing agency amsterdam, online marketing agency, performance marketing agency, Meta Ads agency, TikTok advertising Netherlands, Pinterest Ads agency, Klaviyo partner, AI marketing agency, growth agency, D2C e-commerce marketing, social media advertising, Google Ads agency Amsterdam, e-commerce growth agency, data-driven marketing, conversion optimization, remarketing agency, digital advertising Amsterdam',
  });

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleStartScan = () => {
    setLocation(localePath("/scan"));
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormSubmitted(true);
  };

  if (formSubmitted) {
    return (
      <div className="min-h-screen bg-[#0A1F14] text-white flex flex-col items-center justify-center p-6 text-center relative overflow-hidden">
        <div className="absolute inset-0 z-0 opacity-10">
          <img src={logoW} alt="" aria-hidden="true" className="w-full h-full object-cover" />
        </div>
        <div className="z-10 max-w-xl mx-auto space-y-6">
          <CheckCircle2 className="w-20 h-20 text-[#4CEAAC] mx-auto mb-8" />
          <h1 className="text-4xl md:text-5xl font-display font-bold">{t("thanks_title")}</h1>
          <p className="text-lg text-white/80 font-sans leading-relaxed">{t("thanks_desc")}</p>
          <p className="text-white/60 mb-8">{t("thanks_sub")}</p>
          <Button onClick={() => window.location.reload()} variant="outline" className="border-[#4CEAAC] text-[#4CEAAC] hover:bg-[#4CEAAC] hover:text-[#0A1F14] px-8 py-6 text-lg rounded-full">
            {t("view_case")} <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A1F14] text-white font-sans selection:bg-[#4CEAAC] selection:text-[#0A1F14]" role="main">
      <Navbar activePage="home" />

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center pt-24 md:pt-36 pb-12 overflow-hidden bg-grad-hero has-grain breathing">
        <HeroBackground />

        <div className="container mx-auto px-6 md:px-12 z-10 relative mt-16 md:mt-0">
          <div className="max-w-4xl mx-auto text-center flex flex-col items-center">
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-xs md:text-sm font-semibold tracking-[0.2em] uppercase text-[#4CEAAC] mb-5"
            >
              {nl ? 'Online marketing bureau Amsterdam voor D2C e-commerce groei' : 'AI-Driven Digital Marketing Agency Amsterdam'}
            </motion.h1>
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1 }}
              className="text-4xl md:text-6xl lg:text-7xl font-display font-bold tracking-tight leading-[1.1] mb-6"
              dangerouslySetInnerHTML={{ __html: t("hero_title_1") }}
            />


            
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.3 }}
              className="text-lg md:text-xl text-white/80 max-w-2xl mx-auto leading-relaxed mb-12"
            >
              {t("hero_desc")} <span className="font-bold text-[#4CEAAC]">{t("hero_desc_bold")}</span> {t("hero_desc_2")}
            </motion.p>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="flex flex-col sm:flex-row items-center gap-6 mb-8 md:mb-16"
            >
              <button
                onClick={() => setLocation(localePath("/contact"))}
                className="btn btn--gradient btn--lg"
                data-testid="button-hero-contact"
              >
                {t("hero_cta")} <ArrowRight className="w-5 h-5" />
              </button>
              <button
                onClick={() => setLocation(localePath("/scan"))}
                className="btn btn--outline btn--lg"
                data-testid="button-hero-scan"
              >
                {t("hero_cta2")} <ArrowRight className="w-5 h-5" />
              </button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="flex flex-wrap justify-center items-center gap-4 md:gap-12 text-[10px] md:text-sm text-white/50 font-medium tracking-wider uppercase"
            >
              <span className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[#4CEAAC]"></span>{t("hero_stat_1")}</span>
              <span className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[#4CEAAC]"></span>{t("hero_stat_2")}</span>
              <span className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[#4CEAAC]"></span>{t("hero_stat_3")}</span>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Social Proof Strip */}
      <section id="social-proof" className="py-10 bg-black border-y border-white/5 overflow-hidden">
        <div className="container mx-auto px-6 mb-6 text-center">
          <p className="text-sm font-semibold text-white/50 uppercase tracking-widest">{t("trusted_by")}</p>
        </div>
        
        {/* Logos container */}
        <div className="w-full overflow-hidden flex flex-col justify-center py-6">
          <div className="flex w-max animate-[marquee_30s_linear_infinite] hover:[animation-play-state:paused]">
            {/* Double the array for seamless infinite scrolling */}
            {[...Array(2)].map((_, arrayIndex) => (
              <div key={arrayIndex} aria-hidden={arrayIndex === 1 ? true : undefined} className="flex flex-none items-center justify-center gap-12 md:gap-24 px-6 md:px-12">
                {[
                  { src: stripZevy, alt: "Zèvy logo", h: "h-9 md:h-10" },
                  { src: stripVedder, alt: "Vedder & Vedder logo", h: "h-4 md:h-5" },
                  { src: stripMatt, alt: "Matt Sleeps logo", h: "h-6 md:h-7" },
                  { src: stripAmaya, alt: "Amaya logo", h: "h-6 md:h-7" },
                  { src: stripNewhouse, alt: "Newhouse logo", h: "h-5 md:h-6" },
                  { src: stripFeather, alt: "Feather Women logo", h: "h-9 md:h-10" },
                  { src: stripFranky, alt: "Franky Amsterdam logo", h: "h-6 md:h-7" },
                  { src: stripEquals, alt: "Equals logo", h: "h-6 md:h-7" },
                  { src: stripBrinckers, alt: "Brinckers logo", h: "h-9 md:h-10" },
                  { src: stripJuridischLoket, alt: "Het Juridisch Loket logo", h: "h-9 md:h-10" },
                  { src: stripJutkaRiska, alt: "Jutka & Riska logo", h: "h-4 md:h-5" },
                  { src: stripMimi, alt: "Mimi et Toi logo", h: "h-9 md:h-10" },
                  { src: stripPigHen, alt: "Pig & Hen logo", h: "h-9 md:h-10" },
                  { src: stripModdern, alt: "Moddern logo", h: "h-6 md:h-7" }
                ].map((brand, i) => (
                  <div 
                    key={`${arrayIndex}-${i}`} 
                    className="flex-shrink-0 h-12 md:h-14 flex items-center justify-center transition-transform duration-300 hover:scale-110 opacity-60 hover:opacity-100"
                  >
                    <img 
                      src={brand.src} 
                      alt={arrayIndex === 0 ? brand.alt : ""}
                      loading="lazy"
                      decoding="async"
                      style={{ filter: 'grayscale(1) invert(1) contrast(1.25)', mixBlendMode: 'screen' }}
                      className={`${brand.h} w-auto object-contain transition-all duration-300`}
                    />
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Approach Section - Two Pillars */}
      <section className="py-24 relative bg-grad-ink border-t border-white/5">
        <div className="container mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="text-center max-w-3xl mx-auto mb-16"
          >
            <h3 className="text-[#4CEAAC] text-xs font-semibold tracking-[0.18em] uppercase mb-4">{nl ? 'Onze aanpak' : 'Our approach'}</h3>
            <h2 className="text-4xl md:text-[56px] font-display font-bold leading-[1.04] tracking-[-0.03em]">
              {nl ? <>Senior expertise. <span className="text-[#4CEAAC]">Eigen AI.</span><br />Bewezen resultaat.</> : <>Senior expertise. <span className="text-[#4CEAAC]">Proprietary AI.</span><br />Proven results.</>}
            </h2>
            <p className="text-lg text-white/[0.68] mt-6 leading-[1.65]">
              {nl
                ? <>Onze klanten blijven gemiddeld <MonthsCounter /> maanden. Dit is waarom:</>
                : <>Our clients stay an average of <MonthsCounter /> months. This is why:</>}
            </p>
          </motion.div>
          {/* Desktop: rustige 2x2 grid, geen carousel */}
          <div className="hidden lg:grid lg:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {approachPillars.map((pillar, i) => (
              <ApproachCard key={i} pillar={pillar} index={i} nl={nl} />
            ))}
          </div>
          {/* Mobiel/tablet: swipebare slider met compacte kaarten */}
          <div className="lg:hidden">
            <Carousel setApi={setApproachApi} opts={{ align: "start" }} className="max-w-2xl mx-auto px-4 sm:px-0">
              <CarouselContent className="-ml-4">
                {approachPillars.map((pillar, i) => (
                  <CarouselItem key={i} className="pl-4 basis-[85%] sm:basis-[55%]">
                    <ApproachCard pillar={pillar} index={i} nl={nl} />
                  </CarouselItem>
                ))}
              </CarouselContent>
            </Carousel>
            <div className="mt-8 flex justify-center gap-2">
              {Array.from({ length: approachCount }).map((_, i) => (
                <button
                  key={i}
                  type="button"
                  aria-label={(nl ? "Ga naar kaart " : "Go to card ") + (i + 1)}
                  onClick={() => approachApi?.scrollTo(i)}
                  className={`h-2 rounded-full transition-all duration-300 ${i === approachIndex ? "w-6 bg-[#4CEAAC]" : "w-2 bg-white/25 hover:bg-white/45"}`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Proof Section - Uniform Glass Cards */}
      <section className="py-24 relative bg-black">
        <div className="container mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="flex flex-col md:flex-row justify-between items-end mb-16 gap-6"
          >
            <div className="max-w-2xl">
              <h3 className="text-[#4CEAAC] text-xs font-semibold tracking-[0.18em] uppercase mb-4">{t("proof_label")}</h3>
              <h2 className="text-4xl md:text-[56px] font-display font-bold leading-[1.04] tracking-[-0.03em]">
                {t("proof_title_1")} <span className="text-white [text-shadow:0_0_10px_rgba(76,234,172,0.6)]">{t("proof_title_2")}</span>
              </h2>
            </div>
          </motion.div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                slug: "moddern",
                brand: "Moddern",
                vertical: "Outdoor & Lifestyle",
                desc: nl ? "Nederlands kamado-merk, gelanceerd in 2024. Full-funnel aanpak vanaf seizoen één: Meta, Google Ads, CRO en Klaviyo." : "Dutch kamado brand, launched in 2024. Full-funnel approach from season one: Meta, Google Ads, CRO and Klaviyo.",
                quote: nl ? "Vanaf het eerste gesprek was duidelijk dat ze zowel de creatieve als de commerciële kant van het schalen van een premium merk begrijpen." : "From the first call, it was clear they understand both the creative and the commercial side of scaling a premium brand.",
                quoteName: "Alrik Kielman", quoteRole: nl ? "Founder Moddern" : "Founder Moddern", quoteImg: avatarAlrik,
                img: imgModdernCase,
                imgPos: "object-center",
                kpis: [
                  { value: "3,5", label: "ROAS MEI" },
                  { value: "4x", label: nl ? "ORDERS APR \u2192 MEI" : "ORDERS APR \u2192 MAY" },
                  { value: "1 mln+", label: nl ? "VERTONINGEN" : "IMPRESSIONS" },
                ],
              },
              {
                slug: "mimi-toi",
                brand: "MIMI ET TOI",
                vertical: "Jewelry & Accessories",
                desc: t("case_mimi_desc"),
                quote: nl ? "De expertise van Wrkt Digital helpt MIMI ET TOI naar het volgende niveau." : "Wrkt Digital's expertise is helping MIMI ET TOI to the next level.",
                quoteName: "Mimi van Dijk", quoteRole: nl ? "Founder MIMI ET TOI" : "Founder MIMI ET TOI", quoteImg: avatarMimi,
                img: imgMimiDetail,
                imgPos: "object-center",
                kpis: [
                  { value: "+78%", label: t("kpi_revenue") },
                  { value: "+80%", label: "ROAS" },
                  { value: "+85%", label: t("kpi_new_customers") },
                ],
              },
              {
                slug: "amaya-amsterdam",
                brand: "Studio Amaya",
                vertical: "Fashion & Lifestyle",
                desc: t("case_amaya_desc"),
                quote: nl ? "Door de juiste targeting, budgettering en strategie verdubbelen we ieder jaar weer onze omzet en bereik." : "Through the right targeting, budgeting, and strategy, we double our revenue and reach every year.",
                quoteName: "Pien Ernst", quoteRole: nl ? "Eigenaar Amaya Amsterdam & Studio Amaya" : "Owner Amaya Amsterdam & Studio Amaya", quoteImg: avatarPien,
                img: imgAmayaCard,
                imgPos: "object-center",
                kpis: [
                  { value: "3x", label: nl ? "OMZET 2023" : "REVENUE 2023" },
                  { value: "+67%", label: nl ? "AANKOPEN PAID" : "PAID PURCHASES" },
                  { value: "+40%", label: "ROAS" },
                ],
              },
              {
                slug: "franky-amsterdam",
                brand: "Franky Amsterdam",
                vertical: "Jewelry & Accessories",
                desc: t("case_franky_desc"),
                quote: nl ? "Ik werk nu een paar jaar samen met Wrkt en ik ben erg positief! Ze helpen bij veel meer dan alleen de advertenties." : "I've been working with Wrkt for a few years now and I'm very positive! They help with much more than just the ads.",
                quoteName: "Franka Vedder", quoteRole: nl ? "Founder Franky Amsterdam" : "Founder Franky Amsterdam", quoteImg: avatarFranka,
                img: imgJewelry,
                imgPos: "object-center",
                kpis: [
                  { value: "+40%", label: "ROAS (YOY)" },
                  { value: "-18%", label: nl ? "KOSTEN PER AANKOOP" : "COST PER PURCHASE" },
                  { value: "+18%", label: nl ? "OMZET UIT PAID" : "PAID REVENUE" },
                ],
              },
              {
                slug: "feather-women",
                brand: "Feather Women",
                vertical: "Beauty & Cosmetics",
                desc: t("case_feather_desc"),
                quote: nl ? "Wrkt gaat verder dan adverteren alleen: forecasts, strategisch advies en een proactieve drive voor het beste resultaat." : "Wrkt goes beyond advertising alone: forecasts, strategic advice and a proactive drive for the best results.",
                quoteName: "Anne & Esther Vedder", quoteRole: nl ? "Founders Feather Women" : "Founders Feather Women", quoteImg: avatarVedder,
                img: imgFeather,
                imgPos: "object-center",
                kpis: [
                  { value: "7+", label: "ROAS META" },
                  { value: "+90%", label: t("kpi_revenue") },
                  { value: "5", label: nl ? "MAANDEN" : "MONTHS" },
                ],
              },
              {
                slug: "vedder-vedder",
                brand: "Vedder & Vedder",
                vertical: nl ? "Fashion & Sieraden" : "Fashion & Jewelry",
                desc: nl ? "Partner van 2016 tot 2021 - van gepassioneerde start-up naar internationaal merk in het VK, Duitsland en Frankrijk." : "Partner from 2016 to 2021 - from passionate start-up to international brand in the UK, Germany and France.",
                quote: nl ? "Met de hulp van Wrkt zijn we flink gegroeid, van gepassioneerde start-up naar een merk met internationale ambities." : "With the help of Wrkt we have grown significantly, from passionate start-up to a brand with international ambitions.",
                quoteName: "Anne & Esther Vedder", quoteRole: nl ? "Founders Vedder & Vedder" : "Founders Vedder & Vedder", quoteImg: avatarVedder,
                img: imgVedderCase,
                imgPos: "object-[50%_20%]",
                kpis: [
                  { value: "+100%", label: nl ? "OMZET PER JAAR" : "REVENUE PER YEAR" },
                  { value: "5 jr", label: nl ? "OP RIJ" : "IN A ROW" },
                  { value: "3", label: nl ? "NIEUWE MARKTEN" : "NEW MARKETS" },
                ],
              },
            ].map((c, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.1, type: 'spring', stiffness: 120 }}
                whileHover={{ y: -8 }}
                onClick={() => { if (c.slug) setLocation(localePath(`/cases/${c.slug}`)); }}
                className={`relative group flex flex-col overflow-hidden rounded-2xl border border-white/5 bg-white/[0.02] hover:border-[#4CEAAC]/30 transition-all duration-500 hover:shadow-[0_0_30px_rgba(76,234,172,0.15)] ${c.slug ? 'cursor-pointer' : ''}`} data-testid={`case-card-${i}`}>
                <div className="relative h-56 md:h-64 overflow-hidden">
                  <img src={c.img} alt={`${c.brand} case Wrkt Digital`} loading="lazy" decoding="async" className={`absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105 ${c.imgPos}`} />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
                </div>
                <div className="flex flex-1 flex-col p-6 md:p-7">
                  <span className="text-[11px] text-[#4CEAAC] uppercase tracking-widest font-semibold">{c.vertical}</span>
                  <h3 className="text-2xl font-display font-bold text-white mt-2 mb-3">{c.brand}</h3>
                  <p className="text-white/55 text-sm leading-relaxed mb-4">{c.desc}</p>
                  {c.quote && (
                    <div className="mb-4">
                      <p className="text-white/75 text-sm italic leading-relaxed">&ldquo;{c.quote}&rdquo;</p>
                      <div className="mt-3 flex items-center gap-3">
                        {c.quoteImg ? (
                          <img src={c.quoteImg} alt={c.quoteName} loading="lazy" className="h-9 w-9 shrink-0 rounded-full object-cover border border-[#4CEAAC]/30" />
                        ) : (
                          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#4CEAAC]/15 border border-[#4CEAAC]/30 text-[10px] font-bold text-[#4CEAAC]">{c.quoteName.split(' ').filter((w) => w && w !== '&').map((w) => w[0]).slice(0, 2).join('').toUpperCase()}</span>
                        )}
                        <span className="text-xs leading-tight text-white/45"><span className="font-semibold text-white/80">{c.quoteName}</span><br/>{c.quoteRole}</span>
                      </div>
                    </div>
                  )}
                  {c.kpis.length > 0 && (
                    <div className="mt-auto border-t border-white/10 pt-5 flex gap-6">
                      {c.kpis.map((kpi, j) => (
                        <div key={j}>
                          <div className="text-2xl md:text-3xl font-display font-extrabold text-[#4CEAAC]">{kpi.value}</div>
                          <div className="text-[10px] text-white/45 uppercase tracking-wider font-semibold mt-1">{kpi.label}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 relative bg-black border-t border-white/5">
        <div className="container mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="text-center max-w-3xl mx-auto mb-16"
          >
            <h3 className="text-[#4CEAAC] text-xs font-semibold tracking-[0.18em] uppercase mb-4">{nl ? 'Diensten' : 'Services'}</h3>
            <h2 className="text-4xl md:text-[56px] font-display font-bold leading-[1.04] tracking-[-0.03em]">
              {nl ? <>Alles wat je groei nodig heeft.<br /><span className="text-[#4CEAAC]">In één aanpak.</span></> : <>Everything your growth needs.<br /><span className="text-[#4CEAAC]">In one approach.</span></>}
            </h2>
            <p className="text-lg text-white/[0.68] mt-6 leading-[1.65]">
              {nl
                ? 'Strategie, advertising, creative, e-mail en data, versterkt met Wrkt Signal. Eén team, één plan, in een ritme dat meegroeit met je omzet.'
                : 'Strategy, advertising, creative, email and data, amplified by Wrkt Signal. One team, one plan, at a rhythm that grows with your revenue.'}
            </p>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {(nl ? [
              { icon: Compass, title: "Strategy & Forecasting", desc: "Kwartaalplan, KPI-framework en forecast. We sturen op rendement en je weet elke maand wat de volgende stap is." },
              { icon: Megaphone, title: "Paid Advertising", desc: "Meta, Google en TikTok vanuit één funnelstrategie. We testen, optimaliseren en schalen wat rendeert." },
              { icon: Sparkles, title: "Creative & content optimalisatie", desc: "We ontwikkelen creatives die converteren en on brand zijn. We testen continu varianten en schalen de best performers." },
              { icon: Mail, title: "CRM & Retention", desc: "Klaviyo-flows en campagnes die herhaalaankopen en klantwaarde laten groeien, van welcome tot winback." },
              { icon: BarChart3, title: "Data & Dashboards", desc: "Eén live dashboard voor al je kanalen. Je volgt dagelijks wat elke euro oplevert." },
              { icon: Radar, title: "Wrkt Signal", desc: "Onze eigen AI leest jouw markt: creatives, concurrenten en trends. Standaard in elk pakket." },
            ] : [
              { icon: Compass, title: "Strategy & Forecasting", desc: "Quarterly plan, KPI framework and forecast. We steer on returns, and every month you know what the next step is." },
              { icon: Megaphone, title: "Paid Advertising", desc: "Meta, Google and TikTok from one funnel strategy. We test, optimize and scale what delivers." },
              { icon: Sparkles, title: "Creative & content optimization", desc: "We develop creatives that convert and stay on brand. We continuously test variants and scale best performers." },
              { icon: Mail, title: "CRM & Retention", desc: "Klaviyo flows and campaigns that grow repeat purchases and customer value, from welcome to winback." },
              { icon: BarChart3, title: "Data & Dashboards", desc: "One live dashboard for all your channels. You follow daily what every euro delivers." },
              { icon: Radar, title: "Wrkt Signal", desc: "Our own AI reads your market: creatives, competitors and trends. Included in every package." },
            ]).map((svc, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                whileHover={{ y: -6 }}
                className="rounded-2xl border border-white/5 bg-white/[0.03] p-7 hover:border-[#4CEAAC]/30 transition-all duration-500 hover:shadow-[0_0_30px_rgba(76,234,172,0.12)]"
                data-testid={`service-card-${i}`}
              >
                <svc.icon className="w-5 h-5 text-[#4CEAAC] mb-4" aria-hidden="true" />
                <h4 className="text-lg font-display font-bold mb-2">{svc.title}</h4>
                <p className="text-white/55 text-sm leading-relaxed">{svc.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Creative reel */}
      <section className="py-24 relative bg-black border-t border-white/5 overflow-hidden">
        <div className="container mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 items-center gap-14 max-w-5xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <h3 className="text-[#4CEAAC] text-xs font-semibold tracking-[0.18em] uppercase mb-4">{nl ? 'Creative' : 'Creative'}</h3>
              <h2 className="text-4xl md:text-[56px] font-display font-bold leading-[1.04] tracking-[-0.03em] mb-5">
                {nl ? <>Scroll-stopping <span className="text-[#4CEAAC]">creatives.</span></> : <>Scroll-stopping <span className="text-[#4CEAAC]">creatives.</span></>}
              </h2>
              <p className="text-[16.5px] text-white/70 leading-[1.65] max-w-md">
                {nl
                  ? 'In de feed beslist je klant in \u00e9\u00e9n seconde of jouw ad aandacht krijgt. Creative is daarmee de grootste hefboom in je advertentieresultaat.'
                  : 'In the feed, your customer decides in one second whether your ad gets attention. That makes creative the biggest lever in your advertising results.'}
              </p>
              <p className="text-[16.5px] text-white/70 leading-[1.65] max-w-md mt-4">
                {nl
                  ? 'We analyseren je creatives doorlopend met Wrkt Signal en testen en optimaliseren op hooks, formats en varianten. Wat scoort, schalen we op.'
                  : 'We continuously analyze your creatives with Wrkt Signal, testing and optimizing hooks, formats and variants. What performs, we scale.'}
              </p>
              <ul className="mt-8 space-y-3">
                {(nl ? [
                  'Meer winnende ads',
                  'Ads die langer meegaan',
                  'Elke maand nieuwe inzichten uit je eigen data',
                ] : [
                  'More winning ads',
                  'Ads that last longer',
                  'Fresh insights from your own data every month',
                ]).map((r, i) => (
                  <li key={i} className="flex items-center gap-3 text-white/80">
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-[#4CEAAC] text-[#06140D]">
                      <ArrowRight className="h-3.5 w-3.5 -rotate-45" />
                    </span>
                    {r}
                  </li>
                ))}
              </ul>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.15 }}
              className="flex justify-center"
            >
              <div className="w-[280px] h-[578px] flex-none rounded-[44px] bg-[#111] p-3 shadow-[0_30px_80px_rgba(0,0,0,0.6),inset_0_0_0_2px_rgba(255,255,255,0.08)]">
                <AdReel />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-32 relative overflow-hidden bg-grad-ink border-t border-white/5 breathing">
        <div className="container mx-auto px-6 md:px-12 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="text-center max-w-3xl mx-auto mb-20"
          >
            <h2 className="text-4xl md:text-[56px] font-display font-bold leading-[1.04] tracking-[-0.03em]">
              {nl ? <>Alles verbonden.<br /><span className="text-[#4CEAAC]">Alles meetbaar.</span></> : <>Everything connected.<br /><span className="text-[#4CEAAC]">Everything measurable.</span></>}
            </h2>
            <p className="text-lg text-white/[0.68] mt-6 leading-[1.65] max-w-2xl mx-auto">
              {nl
                ? 'Wrkt Signal koppelt je shop, kanalen en CRM, leest je markt en voedt elke beslissing met data. Jij ziet het resultaat in één dashboard.'
                : 'Wrkt Signal connects your shop, channels and CRM, reads your market and feeds every decision with data. You see the result in one dashboard.'}
            </p>
          </motion.div>
          

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="relative mx-auto mt-24 h-[340px] max-w-3xl"
            aria-hidden="true"
          >
            <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="absolute inset-0 h-full w-full">
              {[[50,10],[85,30],[85,70],[50,90],[15,70],[15,30]].map(([x,y], i) => (
                <line key={i} x1="50" y1="50" x2={x} y2={y} stroke="#4CEAAC" strokeOpacity="0.22" strokeDasharray="3 7" vectorEffect="non-scaling-stroke" className="signal-line" style={{ animationDelay: `${i * 0.4}s` }} />
              ))}
            </svg>
            <div className="core-pulse absolute left-1/2 top-1/2 flex h-40 w-40 -translate-x-1/2 -translate-y-1/2 flex-col items-center justify-center gap-1 rounded-full border border-[#4CEAAC]/50 text-center [background:radial-gradient(circle_at_50%_28%,rgba(37,108,67,0.55)_0%,rgba(7,21,13,0.92)_75%)]">
              <span className="font-display font-bold text-[19px] tracking-[-0.01em] text-white">Wrkt Signal</span>
              <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#4CEAAC]">{nl ? 'AI-kern' : 'AI core'}</span>
            </div>
            {[
              { label: "Shopify", pos: "left-1/2 top-[10%]" },
              { label: "Meta", pos: "left-[85%] top-[30%]" },
              { label: "Google Ads", pos: "left-[85%] top-[70%]" },
              { label: "TikTok Ads", pos: "left-1/2 top-[90%]" },
              { label: "Klaviyo", pos: "left-[15%] top-[70%]" },
              { label: "GA4 & data", pos: "left-[15%] top-[30%]" },
            ].map((n2, i) => (
              <span key={i} className={`absolute ${n2.pos} -translate-x-1/2 -translate-y-1/2 whitespace-nowrap rounded-[10px] border border-white/[0.14] bg-white/[0.05] px-[18px] py-[9px] text-[13.5px] font-semibold text-white/[0.88] backdrop-blur-sm`}>{n2.label}</span>
            ))}
          </motion.div>

          {/* Signal: uitleg + live rapport */}
          <div className="mx-auto mt-28 grid max-w-6xl items-center gap-12 lg:grid-cols-2">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <span className="text-[#4CEAAC] text-xs font-bold tracking-[0.2em] uppercase">{nl ? 'Eigen AI-tooling' : 'Proprietary AI tooling'}</span>
              <h3 className="mt-4 text-2xl md:text-4xl font-display font-bold leading-tight">
                {nl ? <>Blijf de concurrentie voor.<br /><span className="text-[#4CEAAC]">Met Wrkt Signal.</span></> : <>Stay ahead of the competition.<br /><span className="text-[#4CEAAC]">With Wrkt Signal.</span></>}
              </h3>
              <div className="mt-8 space-y-6">
                {(nl ? [
                  { t: 'Je concurrentie in beeld', d: 'Met Signal scannen we razendsnel honderden ads van jouw merk én je concurrenten. Zo zien we welke angles, formats en copy-patronen werken en waar de top spenders in jouw categorie op inzetten.' },
                  { t: 'Testkansen voor morgen', d: 'Elke analyse vertalen we naar concrete tests: bewezen kansen uit je eigen data en nieuwe angles uit je markt. Die zetten we direct om in creatives en campagnes.' },
                  { t: 'Elke advertentie geanalyseerd', d: 'Per ad zien we bereik, looptijd en prestatie. Zo weten we precies welke creatives we moeten ontwikkelen en testen.' },
                ] : [
                  { t: 'Your competition in view', d: 'With Signal we rapidly scan hundreds of ads from your brand and your competitors. We see which angles, formats and copy patterns work, and what the top spenders in your category are betting on.' },
                  { t: 'Tests you can run tomorrow', d: 'We translate every analysis into concrete tests: proven opportunities from your own data and fresh angles from your market, turned directly into creatives and campaigns.' },
                  { t: 'Every ad analyzed', d: 'For each ad we see reach, runtime and performance. So we know exactly which creatives to develop and test.' },
                ]).map((item, i) => (
                  <div key={i} className="flex gap-4">
                    <span className="mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#256C43]/60 text-[11px] font-bold text-[#4CEAAC]">{i + 1}</span>
                    <div>
                      <h4 className="font-display font-bold text-white">{item.t}</h4>
                      <p className="mt-1 text-white/55 leading-relaxed">{item.d}</p>
                    </div>
                  </div>
                ))}
              </div>
              <button
                onClick={() => setLocation(localePath("/contact"))}
                className="btn btn--gradient btn--lg mt-10"
                data-testid="button-signal-demo"
              >
                {nl ? 'Vraag een demo aan' : 'Book a demo'} <ArrowRight className="w-5 h-5" />
              </button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.15 }}
              className="relative"
            >
              <div className="overflow-hidden rounded-2xl border border-white/10 bg-[#06140D] shadow-[0_30px_80px_rgba(0,0,0,0.5)]">
                <div className="flex items-center gap-2 border-b border-white/10 bg-white/[0.04] px-4 py-3">
                  <span className="h-2.5 w-2.5 rounded-full bg-white/15"></span>
                  <span className="h-2.5 w-2.5 rounded-full bg-white/15"></span>
                  <span className="h-2.5 w-2.5 rounded-full bg-white/15"></span>
                  <span className="mx-auto flex items-center gap-1.5 rounded-md bg-white/[0.06] px-3 py-1 text-[11px] text-white/50">
                    <Radar className="h-3 w-3 text-[#4CEAAC]" /> signal.wrkt.nl
                  </span>
                </div>
                <div className="relative h-[520px] overflow-hidden">
                  <img src={signalReport} alt={nl ? 'Wrkt Signal rapport: creative-analyse van een actieve Meta-catalogus' : 'Wrkt Signal report: creative analysis of an active Meta catalog'} loading="eager" className="signal-scroll w-full" />
                  <div className="pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-[#06140D] to-transparent"></div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Offer / CTA Section */}
      <section id="growth-scan-form" className="py-24 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A1F14] to-[#06140D]"></div>
        
        <div className="container mx-auto px-6 md:px-12 relative z-10">
          <div className="max-w-4xl mx-auto bg-[#0A1F14] border border-[#256C43]/50 rounded-3xl p-8 md:p-16 shadow-[0_0_50px_rgba(37,108,67,0.15)] relative overflow-hidden text-center">
            
            {/* Glow effect */}
            <div className="absolute top-0 right-1/2 translate-x-1/2 w-64 h-64 bg-[#4CEAAC]/10 rounded-full blur-[100px] pointer-events-none"></div>
            
            <h3 className="text-[#4CEAAC] text-xs font-semibold tracking-[0.18em] uppercase mb-4">
              {nl ? 'Gratis in 2 minuten' : 'Free in 2 minutes'}
            </h3>
            <h2 className="text-4xl md:text-[56px] font-display font-bold leading-[1.04] tracking-[-0.03em] mb-6">
              {nl ? <>Liever eerst zelf inzicht?<br /><span className="text-[#4CEAAC]">Doe de gratis Growth Scan.</span></> : <>Prefer insight first?<br /><span className="text-[#4CEAAC]">Take the free Growth Scan.</span></>}
            </h2>
            
            <div className="space-y-4 text-white/80 mb-10 leading-relaxed max-w-2xl mx-auto text-lg">
              <p>
                {nl
                  ? 'We analyseren je ad creatives, campagnestructuur en de strategieën van je concurrenten in de Meta Ad Library. Gecombineerd met een check van je tracking en funnel-setup laten we precies zien waar je grootste groeikansen liggen.'
                  : "We analyze your ad creatives, campaign structure and your competitors' strategies in the Meta Ad Library. Combined with a check of your tracking and funnel setup, we pinpoint exactly where your biggest growth gaps are."}
              </p>
            </div>
            
            <div className="max-w-3xl mx-auto mb-10 text-left">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
                {(nl
                  ? ['Ad creative analyse', 'Creative gaps bij concurrenten', 'Campagnestructuur review']
                  : ['Ad creative analysis', 'Competitor creative gaps', 'Campaign structure review']
                ).map((item, i) => (
                  <div key={i} className="flex items-center gap-3 bg-white/[0.02] p-4 rounded-xl border border-white/5 hover:border-[#4CEAAC]/30 hover:bg-[#256C43]/10 hover:shadow-[0_0_15px_rgba(76,234,172,0.08)] transition-all duration-300 cursor-default">
                    <CheckCircle2 className="w-5 h-5 text-[#4CEAAC] flex-shrink-0" />
                    <span className="font-medium text-white/90">{item}</span>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:w-2/3 sm:mx-auto">
                {(nl
                  ? ['Conversion tracking & pixels', 'Technische funnel-setup']
                  : ['Conversion tracking & pixels', 'Technical funnel setup']
                ).map((item, i) => (
                  <div key={i} className="flex items-center gap-3 bg-white/[0.02] p-4 rounded-xl border border-white/5 hover:border-[#4CEAAC]/30 hover:bg-[#256C43]/10 hover:shadow-[0_0_15px_rgba(76,234,172,0.08)] transition-all duration-300 cursor-default">
                    <CheckCircle2 className="w-5 h-5 text-[#4CEAAC] flex-shrink-0" />
                    <span className="font-medium text-white/90">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <Button onClick={() => setLocation(localePath('/scan'))} data-testid="button-start-growth-scan" className="btn btn--gradient btn--xl relative z-20 w-full sm:w-auto">
              <span>{nl ? 'Start je gratis Growth Scan' : 'Get your free Growth Scan'}</span> <ArrowRight className="ml-2 w-5 h-5 flex-shrink-0 group-hover:translate-x-1 transition-transform" />
            </Button>
            
            <p className="text-sm text-white/50 mt-6">{nl ? 'Voor D2C-merken die klaar zijn voor de volgende stap in groei en advertising.' : 'For D2C brands ready for the next step in growth and advertising.'}</p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 bg-black border-t border-white/5">
        <div className="container mx-auto px-6 md:px-12 max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-[56px] font-display font-bold leading-[1.04] tracking-[-0.03em]">{t("faq_title")}</h2>
          </motion.div>
          
          <Accordion type="single" collapsible className="w-full space-y-4">
            {[
              { q: t("faq_q1"), a: (<>{t("faq_a1")} <Link href={localePath("/about")} className="text-[#4CEAAC] hover:underline">{nl ? 'Lees meer over ons →' : 'Learn more about us →'}</Link></>)},
              { q: t("faq_q2"), a: t("faq_a2") },
              { q: t("faq_q3"), a: t("faq_a3") },
              { q: t("faq_q4"), a: t("faq_a4") },
              { q: t("faq_q5"), a: (<>{t("faq_a5")} <Link href={localePath("/scan")} className="text-[#4CEAAC] hover:underline">{nl ? 'Start de gratis Growth Scan →' : 'Start the free Growth Scan →'}</Link></>)},
              { q: t("faq_q6"), a: t("faq_a6") },
              { q: t("faq_q7"), a: t("faq_a7") },
              { q: t("faq_q8"), a: (<>{t("faq_a8")} <Link href={localePath("/contact")} className="text-[#4CEAAC] hover:underline">{nl ? 'Neem contact op →' : 'Get in touch →'}</Link></>)}
            ].map((faq, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="border border-white/10 rounded-xl bg-white/[0.02] px-6">
                <AccordionTrigger className="text-left font-bold text-lg py-6 hover:text-[#4CEAAC] hover:no-underline font-display">{faq.q}</AccordionTrigger>
                <AccordionContent className="text-white/70 leading-relaxed pb-6 text-base">
                  {faq.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Personal contact */}
      <ExpertContact />

      <Footer />
    </div>
  );
}